import React from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { WeezHeader, Button, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import MailTemplate from '../models/mailTemplate';
import MailTemplateRule from '../models/mailTemplateRule';
import TemplateSelector from './template-select/templateListLabel';
import UserRightsRender from '../pages/events/dashboard/components/userRightsRender';
import * as Sentry from '@sentry/browser';
import Loading from './loading';

export default function TemplateList({ organization, partnerId = undefined }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();

    const [templates, loadingTemplates] = MailTemplate.useApiModel(
        {
            organizationId: organization.id,
            ...(partnerId && { partnerId: partnerId }),
            key: 'crm-mail' // Some mails can be crm-mail, others can be ticket, we have to decide about one key!
        },
        [partnerId]
    );

    const templateRef = React.useRef();
    const userTemplateRef = React.useRef();

    const [selectedGuestTemplate, setSelectedGuestTemplate] = React.useState(null);
    const [selectedUserTemplate, setSelectedUserTemplate] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const selectTemplate = React.useCallback(() => {
        // set loading make we loose the ref.current for some reasons.
        // setLoading(true);
        const mail_rule = new MailTemplateRule({
            ...(selectedUserTemplate && { ticketing_partner_user_link: selectedUserTemplate }),
            ...(selectedGuestTemplate && { ticketing_partner: selectedGuestTemplate })
        });
        mail_rule
            .save({ organizationId: organization.id, partnerId: partnerId ? partnerId : undefined, key: 'ticketing_partner_user_link' })
            .then(() => {
                toast.success(t('ticket.mailing.toast_success'));
                templateRef.current.syncActiveTemplate();
                userTemplateRef.current.syncActiveTemplate();
            })
            .catch(e => {
                // Catch sentry
                Sentry.captureException(e);
                toast.error(t('ticket.mailing.toast_error'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [organization, selectedUserTemplate, selectedGuestTemplate, templateRef, userTemplateRef]);

    if (loadingTemplates || !templates || loading) {
        return <Loading />;
    }

    return (
        <UserRightsRender>
            <WeezHeader
                title={t('ticket.mailing.title')}
                backLink={{ onClick: () => history.push(partnerId ? `/ticket/O${organization.id}/partners/${partnerId}` : `/ticket/O${organization.id}/events`) }}
                rightComponent={<Button primary label={t('ticket.mailing.save')} onClick={selectTemplate} disabled={!selectedGuestTemplate && !selectedUserTemplate} />}
            />
            <TemplateSelector
                values={templates}
                selectedTemplate={selectedGuestTemplate}
                onSelect={setSelectedGuestTemplate}
                toggleLabel={t('ticket.mailing.toggle-label-guest')}
                showTemplatesCard={true}
                ruleKey="ticketing_partner"
                ref={templateRef}
            />
            <TemplateSelector
                values={templates}
                selectedTemplate={selectedUserTemplate}
                onSelect={setSelectedUserTemplate}
                toggleLabel={t('ticket.mailing.toggle-label-user')}
                showTemplatesCard={false}
                ruleKey="ticketing_partner_user_link"
                ref={userTemplateRef}
            />
        </UserRightsRender>
    );
}
TemplateList.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string
};
